import { Preferences } from "@capacitor/preferences";

/**
 * In-memory cache for sync access after initial load.
 * Using Map for better key lookup/deletion performance.
 */
const cache = new Map<string, string>();
let initialized = false;

/**
 * Pending write promises per key to ensure sequential writes.
 * This prevents race conditions where out-of-order async completions
 * could corrupt persistent storage.
 */
const pendingWrites = new Map<string, Promise<void>>();

/**
 * Initialize storage by loading ALL keys from Preferences into memory cache.
 * Call this once on app startup.
 */
export async function initStorage(): Promise<void> {
  if (initialized) return;

  try {
    const { keys } = await Preferences.keys();
    await Promise.all(
      keys.map(async (key) => {
        const { value } = await Preferences.get({ key });
        if (value !== null) {
          cache.set(key, value);
        }
      }),
    );
    initialized = true;
  } catch (error) {
    console.error("[Storage] Failed to initialize:", error);
  }
}

/**
 * Sync-like storage API that mirrors localStorage.
 * Reads from in-memory cache, writes to both cache and Preferences.
 *
 * Write operations are queued per-key to prevent race conditions:
 * - Writes to the same key are chained sequentially
 * - Stale writes are skipped (only the latest value is persisted)
 * - Errors don't break the chain for subsequent writes
 */
export const storage = {
  /**
   * Get a value (sync, from cache).
   */
  getItem(key: string): string | null {
    return cache.get(key) ?? null;
  },

  /**
   * Set a value (updates cache immediately, persists async).
   * Writes are queued per-key to prevent out-of-order persistence.
   */
  setItem(key: string, value: string): void {
    cache.set(key, value);

    // Chain writes to ensure sequential execution per key
    const prev = pendingWrites.get(key) ?? Promise.resolve();
    const writePromise = prev
      .catch(() => {
        // Don't let previous errors block the chain
        console.warn(`[Storage] Previous write failed for ${key}, continuing with set.`);
      })
      .then(() => {
        // Only persist if this value is still current (not superseded)
        if (cache.get(key) === value) {
          return Preferences.set({ key, value }).catch((err) =>
            console.error(`[Storage] Failed to persist ${key}:`, err),
          );
        }
      });

    pendingWrites.set(key, writePromise);

    // Cleanup after write completes to prevent memory leak
    writePromise.finally(() => {
      if (pendingWrites.get(key) === writePromise) {
        pendingWrites.delete(key);
      }
    });
  },

  /**
   * Remove a value (updates cache immediately, persists async).
   * Removals are queued per-key to prevent out-of-order persistence.
   */
  removeItem(key: string): void {
    cache.delete(key);

    // Chain with any pending writes for this key
    const prev = pendingWrites.get(key) ?? Promise.resolve();
    const writePromise = prev
      .catch(() => {
        // Don't let previous errors block the chain
        console.warn(`[Storage] Previous write failed for ${key}, continuing with remove.`);
      })
      .then(() => {
        // Only remove if key is still absent (not re-added)
        if (!cache.has(key)) {
          return Preferences.remove({ key }).catch((err) =>
            console.error(`[Storage] Failed to remove ${key}:`, err),
          );
        }
      });

    pendingWrites.set(key, writePromise);

    // Cleanup after write completes
    writePromise.finally(() => {
      if (pendingWrites.get(key) === writePromise) {
        pendingWrites.delete(key);
      }
    });
  },

  /** Resolves once every write already made to `key` has reached Preferences, or failed and been logged. */
  async whenPersisted(key: string): Promise<void> {
    await pendingWrites.get(key);
  },

  /**
   * Clear all storage.
   * Waits for pending writes to complete (they'll be skipped since cache is empty),
   * then clears Preferences. Returns a promise that resolves once Preferences
   * have been durably cleared so callers can await before navigating away.
   */
  async clear(): Promise<void> {
    cache.clear();

    // All pending writes will now be skipped because cache is empty
    // Wait for them to complete, then clear Preferences
    try {
      await Promise.all(Array.from(pendingWrites.values()));
    } catch {
      console.warn("[Storage] Some pending writes failed before clear.");
    }

    pendingWrites.clear();

    try {
      await Preferences.clear();
    } catch (err) {
      console.error("[Storage] Failed to clear:", err);
    }
  },

  /**
   * Get all keys in storage (from cache).
   */
  keys(): string[] {
    return Array.from(cache.keys());
  },
};
